import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../api';

const STORAGE_META = {
    STANDARD:    { label: 'Standard',      color: 'bg-green-600/20 text-green-300',   downloadable: true  },
    STANDARD_IA: { label: 'Standard-IA',   color: 'bg-blue-600/20 text-blue-300',     downloadable: true  },
    GLACIER:     { label: 'Glacier',        color: 'bg-purple-600/20 text-purple-300', downloadable: false },
    DEEP_ARCHIVE:{ label: 'Deep Archive',   color: 'bg-indigo-600/20 text-indigo-300', downloadable: false },
};

const StorageBadge = ({ cls }) => {
    const meta = STORAGE_META[cls] || { label: cls || 'Standard', color: 'bg-gray-600/20 text-gray-300' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>{meta.label}</span>;
};

const fmtBytes = (n) => {
    if (!n) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const getToken = async () => {
    const { tokens } = await fetchAuthSession();
    return tokens.idToken.toString();
};

const AdminBackups = () => {
    const { user, isAuthenticated } = useAuth();
    const [tab, setTab] = useState('quarterly');
    const [backups, setBackups] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(null);
    const [downloadError, setDownloadError] = useState('');
    const [showCli, setShowCli] = useState(false);

    const loadBackups = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = await getToken();
            const res = await fetch(`${API_URL}/admin/backups`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setBackups(await res.json());
        } catch (err) {
            setError('Failed to load backups: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated && user?.isAdmin) loadBackups();
    }, [isAuthenticated, user, loadBackups]);

    if (!isAuthenticated) return <Navigate to="/auth" replace />;
    if (!user?.isAdmin) return <Navigate to="/" replace />;

    const handleDownload = async (key) => {
        setDownloading(key);
        setDownloadError('');
        try {
            const token = await getToken();
            const res = await fetch(`${API_URL}/admin/backups/download-url`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ key }),
            });
            const data = await res.json();
            if (!res.ok) {
                setDownloadError(res.status === 409
                    ? `Archived in ${data.storageClass} — restore via AWS Console before downloading.`
                    : data.error || 'Download failed.');
                return;
            }
            window.open(data.url, '_blank');
        } catch (err) {
            setDownloadError(err.message);
        } finally {
            setDownloading(null);
        }
    };

    const currentList = backups?.[tab] ?? [];
    const grouped = currentList.reduce((acc, e) => {
        (acc[e.label] = acc[e.label] || []).push(e);
        return acc;
    }, {});

    const countUnique = (type) => [...new Set((backups?.[type] || []).map(e => e.label))].length;

    const TABS = [
        { id: 'quarterly', label: 'Quarterly', sub: 'Permanent — Deep Archive',    count: countUnique('quarterly') },
        { id: 'monthly',   label: 'Monthly',   sub: '12-month · Glacier',          count: countUnique('monthly')   },
        { id: 'daily',     label: 'Daily',     sub: '30-day · Standard',           count: countUnique('daily')     },
    ];

    return (
        <div className="min-h-screen py-12 px-6">
            <div className="max-w-6xl mx-auto">

                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl px-6 py-4">
                        <Link to="/admin" className="text-amber-400 text-sm hover:underline block mb-1">← Admin Portal</Link>
                        <h1 className="text-2xl font-bold text-white">Dharma Corpus Backups</h1>
                        <p className="text-gray-400 text-sm mt-1">GFS retention · DynamoDB exports · Glacier Deep Archive</p>
                    </div>
                    <button onClick={loadBackups} className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        Refresh
                    </button>
                </div>

                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                    {TABS.map(t => (
                        <div key={t.id} className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-5">
                            <p className="text-gray-400 text-sm">{t.label} Snapshots</p>
                            <p className="text-3xl font-bold text-white mt-1">{loading ? '—' : t.count}</p>
                            <p className="text-gray-500 text-xs mt-1">{t.sub}</p>
                        </div>
                    ))}
                </div>

                {/* Tabs */}
                <div className="flex gap-1 bg-slate-800/50 rounded-lg p-1 mb-6 w-fit">
                    {TABS.map(t => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`px-5 py-2 rounded-md font-medium text-sm transition-colors ${
                                tab === t.id ? 'bg-amber-500 text-white' : 'text-gray-300 hover:text-white hover:bg-slate-700'
                            }`}
                        >
                            {t.label}
                            {!loading && t.count > 0 && <span className="ml-1 opacity-60 text-xs">({t.count})</span>}
                        </button>
                    ))}
                </div>

                {error && <p className="text-red-400 mb-4 text-sm">{error}</p>}
                {downloadError && (
                    <div className="mb-4 p-3 bg-yellow-900/30 border border-yellow-600 rounded-lg text-yellow-300 text-sm">
                        {downloadError}
                    </div>
                )}

                {loading ? (
                    <p className="text-gray-400 text-center py-16">Loading backups…</p>
                ) : Object.keys(grouped).length === 0 ? (
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-12 text-center">
                        <p className="text-gray-400 text-lg">No {tab} backups found.</p>
                        {tab === 'daily' && (
                            <p className="text-gray-500 text-sm mt-2">
                                Daily exports run at 2:00 AM UTC. First backup appears after the next scheduled run.
                            </p>
                        )}
                        {tab === 'quarterly' && (
                            <p className="text-gray-500 text-sm mt-2">
                                Quarterly archives are created on the first day of January, April, July, and October.
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {Object.entries(grouped)
                            .sort((a, b) => b[0].localeCompare(a[0]))
                            .map(([label, entries]) => (
                            <div key={label} className="bg-slate-900/70 backdrop-blur-sm rounded-xl overflow-hidden">
                                <div className="px-6 py-3 border-b border-slate-700 flex items-center justify-between">
                                    <h3 className="text-white font-semibold">{label}</h3>
                                    <span className="text-gray-500 text-xs">{entries.length} table{entries.length !== 1 ? 's' : ''}</span>
                                </div>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-gray-500 text-xs border-b border-slate-800">
                                            <th className="px-5 py-2 text-left font-normal">Table</th>
                                            <th className="px-5 py-2 text-left font-normal">Storage</th>
                                            <th className="px-5 py-2 text-left font-normal">Records</th>
                                            <th className="px-5 py-2 text-left font-normal">Size</th>
                                            <th className="px-5 py-2 text-left font-normal">Status</th>
                                            <th className="px-5 py-2 text-left font-normal">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.map((e, i) => {
                                            const meta = STORAGE_META[e.storageClass] || STORAGE_META.STANDARD;
                                            return (
                                                <tr key={i} className="border-b border-slate-800/40 hover:bg-slate-800/20">
                                                    <td className="px-5 py-3 text-white font-mono text-xs">{e.tableName}</td>
                                                    <td className="px-5 py-3"><StorageBadge cls={e.storageClass} /></td>
                                                    <td className="px-5 py-3 text-gray-300 text-xs">
                                                        {e.itemCount != null ? e.itemCount.toLocaleString() : '—'}
                                                    </td>
                                                    <td className="px-5 py-3 text-gray-300 text-xs">{fmtBytes(e.billedBytes || e.size)}</td>
                                                    <td className="px-5 py-3 text-xs">
                                                        {e.exportStatus === 'COMPLETED' ? (
                                                            <span className="text-green-400">Completed</span>
                                                        ) : e.exportStatus === 'IN_PROGRESS' ? (
                                                            <span className="text-yellow-400">In Progress</span>
                                                        ) : e.exportStatus ? (
                                                            <span className="text-red-400">{e.exportStatus}</span>
                                                        ) : <span className="text-gray-600">—</span>}
                                                    </td>
                                                    <td className="px-5 py-3">
                                                        {meta.downloadable ? (
                                                            <button
                                                                onClick={() => handleDownload(e.manifestKey)}
                                                                disabled={downloading === e.manifestKey}
                                                                className="text-amber-400 hover:text-amber-300 text-xs font-semibold disabled:opacity-40 transition-colors"
                                                            >
                                                                {downloading === e.manifestKey ? 'Fetching…' : 'Manifest ↓'}
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-600 text-xs">Archived</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                )}

                {/* CLI / Restore instructions */}
                <div className="mt-8 bg-slate-900/70 backdrop-blur-sm rounded-xl overflow-hidden">
                    <button
                        onClick={() => setShowCli(v => !v)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-800/30 transition-colors"
                    >
                        <span className="text-white font-semibold text-sm">Full Download & Restore via AWS CLI</span>
                        <span className="text-gray-400 text-sm">{showCli ? '▲' : '▼'}</span>
                    </button>
                    {showCli && (
                        <div className="px-6 pb-6">
                            <p className="text-gray-400 text-sm mb-4">
                                The Manifest ↓ button above downloads the export metadata (record count, timestamp, data file list).
                                To download the actual data files, use the AWS CLI:
                            </p>
                            <pre className="bg-slate-800 rounded-lg p-4 text-green-300 text-xs overflow-x-auto leading-relaxed whitespace-pre">{`# Download all files from a specific daily backup
aws s3 sync s3://buddha-wisdom-backups/exports/daily/2026-05-30/ ./restore/

# Download a quarterly archive (label format: YYYYQn)
aws s3 sync s3://buddha-wisdom-backups/exports/quarterly/2026Q2/ ./restore/2026Q2/

# Glacier objects require a restore first (12-24h for Flexible, up to 48h for Deep Archive)
aws s3api restore-object \\
  --bucket buddha-wisdom-backups \\
  --key "exports/quarterly/2026Q1/mantras/AWSDynamoDB/.../data/file.json.gz" \\
  --restore-request '{"Days":7,"GlacierJobParameters":{"Tier":"Standard"}}'

# After restore completes, check status
aws s3api head-object --bucket buddha-wisdom-backups --key "exports/..."
# Look for: "Restore": "ongoing-request=\\"false\\", expiry-date=\\"...\\""`}
                            </pre>
                            <p className="text-gray-500 text-xs mt-3">
                                All exports use DYNAMODB_JSON format. Use the DynamoDB console Import feature or a custom
                                script with <code className="text-green-300">@aws-sdk/lib-dynamodb</code> to restore to a table.
                            </p>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default AdminBackups;
